// src/features/estoque/components/DicionarioMapeamentoModal.jsx
import React, { useState, useEffect } from "react";
import {
  obterTodosMapeamentos,
  excluirMapeamento,
  salvarMapeamento,
  obterHistoricoNotasCompleto,
  aplicarAjusteEstoqueNota,
  obterTemporadasFechadas,
  observarEstoque,
} from "services/estoqueService";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  BookOpen,
  Search,
  Trash2,
  Edit2,
  Check,
  X,
  AlertTriangle,
  Sparkles,
  History,
  Layers,
  ArrowRight,
  ShieldCheck,
  RefreshCw,
  Plus,
  Lock,
  Package,
  Tag,
} from "lucide-react";
import toast from "react-hot-toast";

export const DicionarioMapeamentoModal = ({
  isOpen,
  onClose,
  estoqueItems: estoqueItemsProp = [],
}) => {
  const [abaAtiva, setAbaAtiva] = useState("dicionario"); // 'dicionario' | 'auditoria'
  const [mapeamentos, setMapeamentos] = useState([]);
  const [historicoNotas, setHistoricoNotas] = useState([]);
  const [temporadasFechadas, setTemporadasFechadas] = useState([]);
  const [mostrarTodasTemporadas, setMostrarTodasTemporadas] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("todos"); // 'todos' | 'revenda' | 'despesa'

  // Lista local de estoque caso a prop venha vazia
  const [listaEstoque, setListaEstoque] = useState(estoqueItemsProp);

  // Estado para edição do item do mapeamento
  const [editandoItem, setEditandoItem] = useState(null);
  // Form de edição: { id, codigoInterno, categoriaContabil, fatorConversao }
  const [formEdicao, setFormEdicao] = useState({
    codigoInterno: "",
    categoriaContabil: "REVENDA",
    fatorConversao: 1,
  });

  // Estado para adicionar vínculo manual
  const [mostrarFormNovo, setMostrarFormNovo] = useState(false);
  const [formNovo, setFormNovo] = useState({
    cnpj: "",
    codigoFornecedor: "",
    nomeProduto: "",
    unidadeFornecedor: "UN",
    codigoInterno: "",
    categoriaContabil: "REVENDA",
    fatorConversao: 1,
  });

  // Carregar produtos de estoque via onSnapshot se não vier por prop
  useEffect(() => {
    if (estoqueItemsProp && estoqueItemsProp.length > 0) {
      setListaEstoque(estoqueItemsProp);
    } else {
      const unsub = observarEstoque((itens) => {
        setListaEstoque(itens || []);
      });
      return () => unsub();
    }
  }, [estoqueItemsProp]);

  // Carregar dados de mapeamentos e histórico ao abrir
  const carregarDados = async () => {
    setLoading(true);
    try {
      const [maps, notas, temps] = await Promise.all([
        obterTodosMapeamentos(),
        obterHistoricoNotasCompleto(),
        obterTemporadasFechadas(),
      ]);
      setMapeamentos(maps || []);
      setHistoricoNotas(notas || []);
      setTemporadasFechadas(temps || []);
    } catch (err) {
      console.error("Erro ao carregar dicionário e histórico:", err);
      toast.error("Erro ao carregar dados do dicionário.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      carregarDados();
      setEditandoItem(null);
      setMostrarFormNovo(false);
    }
  }, [isOpen]);

  const handleExcluir = async (mappingId) => {
    if (!window.confirm("Deseja realmente remover este vínculo do dicionário?")) {
      return;
    }
    try {
      await excluirMapeamento(mappingId);
      setMapeamentos((prev) => prev.filter((m) => m.id !== mappingId));
      if (editandoItem?.id === mappingId) {
        setEditandoItem(null);
      }
      toast.success("Mapeamento removido com sucesso.");
    } catch (err) {
      toast.error("Erro ao excluir mapeamento.");
    }
  };

  // Iniciar edição de um card
  const iniciarEdicao = (m) => {
    setEditandoItem(m);
    setFormEdicao({
      codigoInterno: m.codigo_estoque_interno || "DESP_GERAL",
      categoriaContabil: m.categoria_contabil || (m.codigo_estoque_interno === "DESP_GERAL" ? "INSUMO" : "REVENDA"),
      fatorConversao: m.fator_conversao || 1,
    });
  };

  const cancelarEdicao = () => {
    setEditandoItem(null);
  };

  // Salvar a edição do vínculo
  const handleSalvarEdicao = async (mapOriginal) => {
    const isDesp = formEdicao.codigoInterno === "DESP_GERAL";
    const categoria = isDesp ? "INSUMO" : (formEdicao.categoriaContabil || "REVENDA");
    const fator = isDesp ? 1 : Number(formEdicao.fatorConversao || 1);

    if (!isDesp && (!fator || fator <= 0)) {
      toast.error("O fator de conversão deve ser um número maior que zero (ex: 1 ou 12).");
      return;
    }

    try {
      await salvarMapeamento(
        mapOriginal.cnpj_fornecedor,
        mapOriginal.codigo_fornecedor,
        formEdicao.codigoInterno,
        categoria,
        fator,
        mapOriginal.nome_produto_fornecedor || "",
        mapOriginal.unidade_fornecedor || "UN"
      );

      setMapeamentos((prev) =>
        prev.map((m) =>
          m.id === mapOriginal.id
            ? {
                ...m,
                codigo_estoque_interno: formEdicao.codigoInterno,
                categoria_contabil: categoria,
                fator_conversao: fator,
              }
            : m
        )
      );

      setEditandoItem(null);
      toast.success("Vínculo de produto atualizado com sucesso!", { icon: "✅" });
    } catch (err) {
      console.error("Erro ao salvar mapeamento:", err);
      toast.error("Erro ao salvar alterações no mapeamento.");
    }
  };

  // Criar novo vínculo manual
  const handleCriarNovoMapeamento = async () => {
    if (!formNovo.codigoFornecedor || !formNovo.nomeProduto) {
      toast.error("Preencha ao menos o Código e Nome do produto na nota fiscal.");
      return;
    }
    const isDesp = formNovo.codigoInterno === "DESP_GERAL";
    const categoria = isDesp ? "INSUMO" : (formNovo.categoriaContabil || "REVENDA");
    const fator = isDesp ? 1 : Number(formNovo.fatorConversao || 1);

    try {
      await salvarMapeamento(
        formNovo.cnpj.replace(/\D/g, "") || "00000000000000",
        formNovo.codigoFornecedor.trim(),
        formNovo.codigoInterno || "DESP_GERAL",
        categoria,
        fator,
        formNovo.nomeProduto.trim(),
        formNovo.unidadeFornecedor.trim().toUpperCase() || "UN"
      );

      toast.success("Novo vínculo cadastrado no dicionário!", { icon: "🎉" });
      setMostrarFormNovo(false);
      setFormNovo({
        cnpj: "",
        codigoFornecedor: "",
        nomeProduto: "",
        unidadeFornecedor: "UN",
        codigoInterno: "",
        categoriaContabil: "REVENDA",
        fatorConversao: 1,
      });
      carregarDados();
    } catch (err) {
      console.error("Erro ao cadastrar novo vínculo:", err);
      toast.error("Erro ao salvar novo vínculo.");
    }
  };

  // Localizar o nome do item no estoque interno
  const getNomeItemInterno = (codigoInterno) => {
    if (!codigoInterno || codigoInterno === "DESP_GERAL") {
      return "Não Mapear (Despesa Geral / Sem Estoque)";
    }
    const item = listaEstoque.find(
      (e) => String(e.codigo) === String(codigoInterno) || String(e.id) === String(codigoInterno)
    );
    return item ? `${item.nome} (${item.codigo})` : `Código: ${codigoInterno}`;
  };

  // Localizar o objeto completo do item no estoque interno
  const getItemEstoque = (codigoInterno) => {
    if (!codigoInterno || codigoInterno === "DESP_GERAL") return null;
    return listaEstoque.find(
      (e) => String(e.codigo) === String(codigoInterno) || String(e.id) === String(codigoInterno)
    );
  };

  // Itens filtrados do dicionário
  const mapeamentosFiltrados = mapeamentos.filter((m) => {
    const termo = busca.toLowerCase();
    const nomeForn = (m.nome_produto_fornecedor || "").toLowerCase();
    const codForn = (m.codigo_fornecedor || "").toLowerCase();
    const cnpjForn = (m.cnpj_fornecedor || "").toLowerCase();
    const codInt = (m.codigo_estoque_interno || "").toLowerCase();
    const nomeInt = getNomeItemInterno(m.codigo_estoque_interno).toLowerCase();

    const matchesTermo =
      nomeForn.includes(termo) ||
      codForn.includes(termo) ||
      cnpjForn.includes(termo) ||
      codInt.includes(termo) ||
      nomeInt.includes(termo);

    if (!matchesTermo) return false;

    const isDespesa = m.codigo_estoque_interno === "DESP_GERAL" || m.categoria_contabil === "INSUMO";
    if (filtroCategoria === "revenda" && isDespesa) return false;
    if (filtroCategoria === "despesa" && !isDespesa) return false;

    return true;
  });

  // Data de corte da temporada atual (fechamento mais recente)
  const ultimaTemporadaFechada =
    temporadasFechadas && temporadasFechadas.length > 0 ? temporadasFechadas[0] : null;
  const dataCorte = ultimaTemporadaFechada?.dataFechamento?.toDate
    ? ultimaTemporadaFechada.dataFechamento.toDate()
    : ultimaTemporadaFechada?.dataFechamento
    ? new Date(ultimaTemporadaFechada.dataFechamento)
    : null;

  // Localizar o nome do produto no histórico de notas caso o vínculo não tenha nome gravado
  const getNomeProdutoFornecedor = (m) => {
    if (m.nome_produto_fornecedor && m.nome_produto_fornecedor.trim()) {
      return m.nome_produto_fornecedor;
    }
    const codForn = String(m.codigo_fornecedor || "").trim();
    const cnpjForn = String(m.cnpj_fornecedor || "").replace(/\D/g, "");

    for (const nota of historicoNotas) {
      if (!Array.isArray(nota.itens)) continue;
      const cnpjNota = (nota.chave_acesso && nota.chave_acesso.length === 44)
        ? nota.chave_acesso.substring(6, 20)
        : (nota.cnpj_emitente || "").replace(/\D/g, "");

      if (cnpjForn && cnpjNota && cnpjForn !== cnpjNota) continue;

      const itemEncontrado = nota.itens.find(
        (it) => String(it.codigo).trim() === codForn && it.nome
      );
      if (itemEncontrado && itemEncontrado.nome) {
        return itemEncontrado.nome;
      }
    }
    return `Cód. ${m.codigo_fornecedor}`;
  };

  // Calcula quanto este código de produto específico forneceu para o estoque geral nesta temporada
  const getQtdContribuicaoProduto = (mapping) => {
    if (!mapping || mapping.codigo_estoque_interno === "DESP_GERAL" || mapping.categoria_contabil === "INSUMO") {
      return 0;
    }

    const codForn = String(mapping.codigo_fornecedor || "").trim();
    const cnpjForn = String(mapping.cnpj_fornecedor || "").replace(/\D/g, "");
    const nomeForn = (mapping.nome_produto_fornecedor || "").trim().toLowerCase();
    const fatorMapping = Number(mapping.fator_conversao) || 1;

    let totalEntradas = 0;

    historicoNotas.forEach((nota) => {
      if (nota.tipo_operacao !== "Entrada" || !Array.isArray(nota.itens)) return;

      // Se houver temporada fechada, considerar apenas notas da temporada atual
      if (dataCorte && nota.data_emissao) {
        const dataEmissao = new Date(nota.data_emissao);
        if (dataEmissao < dataCorte) return;
      }

      const cnpjNota = (nota.chave_acesso && nota.chave_acesso.length === 44)
        ? nota.chave_acesso.substring(6, 20)
        : (nota.cnpj_emitente || "").replace(/\D/g, "");

      if (cnpjForn && cnpjNota && cnpjForn !== cnpjNota) return;

      nota.itens.forEach((it) => {
        const codItem = String(it.codigo || "").trim();
        const nomeItem = (it.nome || "").trim().toLowerCase();

        const match = (codForn && codItem === codForn) || (!codForn && nomeForn && nomeItem === nomeForn);
        if (!match) return;

        // Fator de multiplicação atual definido no dicionário
        const fatorConversao = fatorMapping || Number(it.fator_conversao) || 1;

        // Quantidade de embalagens/caixas faturadas pelo fornecedor
        let qtdEmbalagens = Number(it.quantidade_original ?? it.quantidade ?? it.qCom ?? 0);
        if (qtdEmbalagens === 0 && it.quantidade_estoque) {
          const fatorGravado = Number(it.fator_conversao) || 1;
          qtdEmbalagens = Number(it.quantidade_estoque) / fatorGravado;
        }

        // Valor final com o fator de multiplicação aplicado (ex: 100 packs x 2 = 200 un)
        const qtdFinal = Math.round(qtdEmbalagens * fatorConversao);
        totalEntradas += qtdFinal;
      });
    });

    return totalEntradas;
  };


  // IDENTIFICAÇÃO DE DISCREPÂNCIAS NAS NOTAS JÁ IMPORTADAS (Auditoria /expert)
  // Detecta tanto itens que foram ignorados como INSUMO/DESP_GERAL (onde 0 un entraram no estoque físico)
  // quanto itens faturados em dúzia/fardo onde o fator gravado foi menor que o fator atual do dicionário.
  const itensAuditoria = [];
  historicoNotas.forEach((nota) => {
    if (nota.tipo_operacao === "Entrada" && Array.isArray(nota.itens)) {
      const dataEmissaoNota = nota.data_emissao ? new Date(nota.data_emissao) : null;
      const pertenceTemporadaFechada = Boolean(
        dataCorte && dataEmissaoNota && dataEmissaoNota < dataCorte
      );

      nota.itens.forEach((it) => {
        // Encontra o vínculo correspondente no dicionário
        // Se o mapeamento tiver CNPJ, ele SÓ pode pertencer a notas deste mesmo CNPJ (evita colisão de códigos entre fornecedores)
        const cnpjNota =
          nota.chave_acesso && nota.chave_acesso.length === 44
            ? nota.chave_acesso.substring(6, 20)
            : "";

        const mappingCorrespondente = mapeamentos.find((m) => {
          if (m.cnpj_fornecedor && cnpjNota) {
            if (m.cnpj_fornecedor !== cnpjNota) return false;
            return (
              String(m.codigo_fornecedor).trim() === String(it.codigo).trim() ||
              (it.nome &&
                m.nome_produto_fornecedor &&
                m.nome_produto_fornecedor.trim().toLowerCase() === it.nome.trim().toLowerCase())
            );
          }

          // Se o mapeamento não tem CNPJ cadastrado, compara por código e nome
          return (
            (it.codigo && String(m.codigo_fornecedor).trim() === String(it.codigo).trim()) ||
            (it.nome &&
              m.nome_produto_fornecedor &&
              m.nome_produto_fornecedor.trim().toLowerCase() === it.nome.trim().toLowerCase())
          );
        });

        if (!mappingCorrespondente) return;
        if (it.ajuste_conversao_aplicado) return;

        // Se no dicionário atual o item está configurado como REVENDA com item interno ativo
        const ehRevendaAtual =
          mappingCorrespondente.categoria_contabil === "REVENDA" &&
          mappingCorrespondente.codigo_estoque_interno &&
          mappingCorrespondente.codigo_estoque_interno !== "DESP_GERAL";

        if (!ehRevendaAtual) return;

        const fatorIdeal = Number(mappingCorrespondente.fator_conversao) || 1;
        const qtdOriginal = Number(it.quantidade_original || it.quantidade) || 0;
        const qtdIdeal = Math.round(qtdOriginal * fatorIdeal);

        // Caso 1: Na nota original foi importado como INSUMO ou DESP_GERAL (logo, ZERO unidades entraram no estoque físico)
        const eraInsumoOuDespesa =
          it.categoria_contabil !== "REVENDA" ||
          it.codigo_interno === "DESP_GERAL";

        let qtdEstoqueGravada = 0;
        let tipoDiscrepancia = "";

        if (eraInsumoOuDespesa) {
          qtdEstoqueGravada = 0; // Insumos não incrementavam o doc de estoque físico
          tipoDiscrepancia = "era_insumo";
        } else {
          // Caso 2: Já era revenda, mas foi gravado com fator menor (ex: 1 un em vez de 12 un)
          const fatorGravado = Number(it.fator_conversao) || 1;
          qtdEstoqueGravada =
            Number(it.quantidade_estoque) || Math.round(qtdOriginal * fatorGravado);
          tipoDiscrepancia = "fator_conversao";
        }

        const discrepancia = qtdIdeal - qtdEstoqueGravada;

        if (discrepancia > 0) {
          itensAuditoria.push({
            chaveNota: nota.chave_acesso,
            dataEmissao: nota.data_emissao,
            codigoFornecedor: it.codigo,
            nomeProduto: it.nome,
            codigoInterno: mappingCorrespondente.codigo_estoque_interno,
            qtdOriginal,
            fatorAtual: eraInsumoOuDespesa ? 0 : (it.fator_conversao || 1),
            fatorIdeal,
            qtdEstoqueGravada,
            qtdIdeal,
            deltaAjustar: discrepancia,
            tipoDiscrepancia,
            categoria: mappingCorrespondente.categoria_contabil,
            pertenceTemporadaFechada,
            nomeTemporadaFechada: ultimaTemporadaFechada?.nome || "Temporada Anterior",
            dataCorteFormatada: dataCorte ? dataCorte.toLocaleDateString("pt-BR") : "",
          });
        }
      });
    }
  });

  const itensAuditoriaTemporadaAtual = itensAuditoria.filter(
    (it) => !it.pertenceTemporadaFechada
  );
  const itensAuditoriaFechados = itensAuditoria.filter(
    (it) => it.pertenceTemporadaFechada
  );
  const itensAuditoriaExibidos = mostrarTodasTemporadas
    ? itensAuditoria
    : itensAuditoriaTemporadaAtual;

  // Ação de auditoria: aplicar o acréscimo retroativo no estoque físico
  const handleCorrigirItemAuditoria = async (itemAudit) => {
    if (itemAudit.pertenceTemporadaFechada) {
      toast.error(
        "Esta nota pertence a uma temporada anterior já encerrada. O estoque dela já foi arquivado e zerado.",
        { icon: "🔒", duration: 5000 }
      );
      return;
    }

    if (!itemAudit.codigoInterno || itemAudit.codigoInterno === "DESP_GERAL") {
      toast.error("Este produto ainda não está vinculado a um item ativo no estoque.");
      return;
    }

    try {
      const res = await aplicarAjusteEstoqueNota(
        itemAudit.chaveNota,
        itemAudit.codigoInterno,
        itemAudit.deltaAjustar,
        itemAudit.nomeProduto,
        itemAudit.codigoFornecedor
      );

      if (res.success) {
        toast.success(
          `Estoque creditado! +${itemAudit.deltaAjustar} unidades adicionadas ao produto no estoque.`,
          { icon: "⚡", duration: 5000 }
        );
        carregarDados();
      } else {
        toast.error(`Falha: ${res.message}`);
      }
    } catch (err) {
      console.error("Erro ao aplicar ajuste de estoque:", err);
      toast.error("Erro ao aplicar correção de estoque.");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-slate-900 text-slate-100 border-slate-800 shadow-2xl">
        {/* Cabeçalho do Modal */}
        <DialogHeader className="p-4 sm:p-6 pb-3 border-b border-slate-800 bg-slate-950/70">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 shrink-0">
                <BookOpen className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-base sm:text-lg font-bold text-slate-100 flex items-center gap-2 truncate">
                  Dicionário de Mapeamento Fiscal
                  <span className="text-[11px] font-mono bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full border border-emerald-500/30">
                    /expert
                  </span>
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-400 mt-0.5 truncate">
                  Gerencie vínculos de bebidas, altere itens que eram despesas para revenda e ajuste fatores de dúzias.
                </DialogDescription>
              </div>
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
              <Button
                variant="ghost"
                size="icon"
                onClick={carregarDados}
                disabled={loading}
                className="text-slate-400 hover:text-slate-200 w-8 h-8"
                title="Recarregar dados"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>

          {/* Seletor de Abas */}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <button
              onClick={() => setAbaAtiva("dicionario")}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                abaAtiva === "dicionario"
                  ? "bg-emerald-500 text-slate-950 shadow-md shadow-emerald-500/20"
                  : "bg-slate-800/80 text-slate-400 hover:text-slate-200 hover:bg-slate-800"
              }`}
            >
              <Layers className="w-3.5 h-3.5" />
              <span>Vínculos Cadastrados ({mapeamentos.length})</span>
            </button>

            <button
              onClick={() => setAbaAtiva("auditoria")}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all relative ${
                abaAtiva === "auditoria"
                  ? "bg-amber-500 text-slate-950 shadow-md shadow-amber-500/20"
                  : "bg-slate-800/80 text-slate-400 hover:text-slate-200 hover:bg-slate-800"
              }`}
            >
              <History className="w-3.5 h-3.5" />
              <span>Auditoria Retroativa</span>
              {itensAuditoriaTemporadaAtual.length > 0 && (
                <span className="w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
                  {itensAuditoriaTemporadaAtual.length}
                </span>
              )}
            </button>
          </div>
        </DialogHeader>

        {/* CONTEÚDO DA ABA 1: DICIONÁRIO */}
        {abaAtiva === "dicionario" && (
          <div className="flex-1 overflow-hidden flex flex-col p-4 sm:p-6 pt-3 space-y-3">
            {/* Barra de Ações: Busca + Filtros + Botão Novo */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  placeholder="Buscar por produto (ex: agua, heineken), código ou CNPJ..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  className="pl-9 bg-slate-950/70 border-slate-800 text-slate-200 text-xs h-9 placeholder:text-slate-500 focus-visible:ring-emerald-500"
                />
              </div>

              <div className="flex items-center gap-1.5 shrink-0 overflow-x-auto">
                {/* Filtros de Categoria */}
                <button
                  onClick={() => setFiltroCategoria("todos")}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${
                    filtroCategoria === "todos"
                      ? "bg-slate-800 text-slate-200 border-slate-700"
                      : "bg-transparent text-slate-500 border-transparent hover:text-slate-300"
                  }`}
                >
                  Todos
                </button>
                <button
                  onClick={() => setFiltroCategoria("revenda")}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${
                    filtroCategoria === "revenda"
                      ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"
                      : "bg-transparent text-slate-500 border-transparent hover:text-slate-300"
                  }`}
                >
                  Revenda
                </button>
                <button
                  onClick={() => setFiltroCategoria("despesa")}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${
                    filtroCategoria === "despesa"
                      ? "bg-rose-500/20 text-rose-300 border-rose-500/30"
                      : "bg-transparent text-slate-500 border-transparent hover:text-slate-300"
                  }`}
                >
                  Não Mapeados / Insumos
                </button>

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setMostrarFormNovo(!mostrarFormNovo)}
                  className="h-8 text-xs border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Novo Vínculo
                </Button>
              </div>
            </div>

            {/* FORMULÁRIO DE CADASTRO NOVO VÍNCULO (Expansível) */}
            {mostrarFormNovo && (
              <div className="bg-slate-950 border border-emerald-500/40 rounded-xl p-3.5 text-xs space-y-3 shadow-lg">
                <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                  <h4 className="font-bold text-emerald-400 flex items-center gap-1.5">
                    <Plus className="w-3.5 h-3.5" />
                    Cadastrar Novo Vínculo de Fornecedor
                  </h4>
                  <button
                    onClick={() => setMostrarFormNovo(false)}
                    className="text-slate-500 hover:text-slate-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  <div>
                    <label className="text-[10px] text-slate-400 block mb-1">
                      Código do Produto na Nota
                    </label>
                    <Input
                      placeholder="Ex: 12345"
                      value={formNovo.codigoFornecedor}
                      onChange={(e) =>
                        setFormNovo({ ...formNovo, codigoFornecedor: e.target.value })
                      }
                      className="h-8 text-xs bg-slate-900 border-slate-800"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="text-[10px] text-slate-400 block mb-1">
                      Descrição do Produto na Nota
                    </label>
                    <Input
                      placeholder="Ex: AGUA MINERAL C/ GAS 510ML"
                      value={formNovo.nomeProduto}
                      onChange={(e) =>
                        setFormNovo({ ...formNovo, nomeProduto: e.target.value })
                      }
                      className="h-8 text-xs bg-slate-900 border-slate-800"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] text-slate-400 block mb-1">
                      CNPJ do Fornecedor (opcional)
                    </label>
                    <Input
                      placeholder="Apenas números ou deixe em branco"
                      value={formNovo.cnpj}
                      onChange={(e) =>
                        setFormNovo({ ...formNovo, cnpj: e.target.value })
                      }
                      className="h-8 text-xs bg-slate-900 border-slate-800"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] text-slate-400 block mb-1">
                      Unidade na Nota
                    </label>
                    <Input
                      placeholder="UN, CX, EB, DZ..."
                      value={formNovo.unidadeFornecedor}
                      onChange={(e) =>
                        setFormNovo({ ...formNovo, unidadeFornecedor: e.target.value })
                      }
                      className="h-8 text-xs bg-slate-900 border-slate-800 uppercase"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] text-slate-400 block mb-1">
                      Fator de Conversão (1 Embalagem = X un)
                    </label>
                    <Input
                      type="number"
                      min="1"
                      placeholder="1 para avulso, 12 para dúzia"
                      value={formNovo.fatorConversao}
                      onChange={(e) =>
                        setFormNovo({ ...formNovo, fatorConversao: e.target.value })
                      }
                      className="h-8 text-xs bg-slate-900 border-slate-800 font-bold text-amber-400"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="text-[10px] text-slate-400 block mb-1">
                      Vincular ao Produto no Estoque do Parque
                    </label>
                    <select
                      value={formNovo.codigoInterno}
                      onChange={(e) =>
                        setFormNovo({
                          ...formNovo,
                          codigoInterno: e.target.value,
                          categoriaContabil:
                            e.target.value === "DESP_GERAL" ? "INSUMO" : "REVENDA",
                        })
                      }
                      className="w-full h-8 px-2 text-xs rounded-md bg-slate-900 border border-slate-800 text-slate-200 focus:ring-1 focus:ring-emerald-500"
                    >
                      <option value="">-- Selecione o produto no estoque --</option>
                      <option value="DESP_GERAL">
                        ❌ Não mapear (Despesa Geral / Sem Estoque Físico)
                      </option>
                      <optgroup label="Itens Ativos no Estoque">
                        {listaEstoque.map((item) => {
                          const qtd = Number(item.quantidadeAtual || 0).toLocaleString("pt-BR");
                          return (
                            <option key={item.id || item.codigo} value={item.codigo}>
                              {item.nome} (Cód: {item.codigo} • Estoque: {qtd} un)
                            </option>
                          );
                        })}
                      </optgroup>
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] text-slate-400 block mb-1">
                      Destino Contábil
                    </label>
                    <select
                      value={formNovo.categoriaContabil}
                      disabled={formNovo.codigoInterno === "DESP_GERAL"}
                      onChange={(e) =>
                        setFormNovo({ ...formNovo, categoriaContabil: e.target.value })
                      }
                      className="w-full h-8 px-2 text-xs rounded-md bg-slate-900 border border-slate-800 text-slate-200 focus:ring-1 focus:ring-emerald-500"
                    >
                      <option value="REVENDA">🛒 Revenda (Entra no estoque)</option>
                      <option value="INSUMO">🏢 Insumo / Despesa (Não entra)</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setMostrarFormNovo(false)}
                    className="h-7 text-xs text-slate-400 hover:text-slate-200"
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleCriarNovoMapeamento}
                    className="h-7 text-xs bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold"
                  >
                    Salvar Novo Vínculo
                  </Button>
                </div>
              </div>
            )}

            {/* Lista de Mapeamentos */}
            <div className="flex-1 overflow-y-auto space-y-2.5 pr-1">
              {loading ? (
                <div className="text-center py-12 text-slate-500 text-xs">
                  Carregando dicionário...
                </div>
              ) : mapeamentosFiltrados.length === 0 ? (
                <div className="text-center py-12 text-slate-500 text-xs border border-dashed border-slate-800 rounded-xl p-6">
                  {busca
                    ? "Nenhum produto encontrado para o termo pesquisado."
                    : "Nenhum vínculo cadastrado ainda no dicionário."}
                </div>
              ) : (
                mapeamentosFiltrados.map((m) => {
                  const isEditando = editandoItem?.id === m.id;
                  const isDespesa =
                    m.codigo_estoque_interno === "DESP_GERAL" ||
                    m.categoria_contabil === "INSUMO";
                  const itemInternoNome = getNomeItemInterno(m.codigo_estoque_interno);
                  const fatorAtual = m.fator_conversao || 1;

                  return (
                    <div
                      key={m.id}
                      className={`border rounded-xl p-3 sm:p-3.5 text-xs transition-all ${
                        isEditando
                          ? "bg-slate-950 border-emerald-500/60 shadow-lg ring-1 ring-emerald-500/20"
                          : isDespesa
                          ? "bg-slate-950/40 border-slate-800/80 hover:border-slate-700"
                          : "bg-slate-950/70 border-slate-800/90 hover:border-slate-700"
                      }`}
                    >
                      {/* Visualização Padrão do Card */}
                      {!isEditando ? (
                        <div className="space-y-2.5">
                          <div className="flex items-start justify-between gap-2">
                            <div className="space-y-0.5 min-w-0">
                              <h4 className="font-bold text-slate-200 text-sm truncate">
                                {getNomeProdutoFornecedor(m)}
                              </h4>
                              <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-400 font-mono">
                                <span>CNPJ: {m.cnpj_fornecedor || "Não informado"}</span>
                                <span>•</span>
                                <span>Cód: {m.codigo_fornecedor}</span>
                                <span>•</span>
                                <span>Un: {m.unidade_fornecedor || "UN"}</span>
                              </div>
                            </div>

                            <div className="flex items-center gap-1.5 shrink-0">
                              {/* Badge de Categoria */}
                              <span
                                className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                  !isDespesa
                                    ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                                    : "bg-rose-500/20 text-rose-300 border border-rose-500/30"
                                }`}
                              >
                                {!isDespesa
                                  ? `🛒 Revenda (${fatorAtual}x)`
                                  : "❌ Não Mapeado (Despesa)"}
                              </span>

                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleExcluir(m.id)}
                                className="w-7 h-7 text-slate-500 hover:text-rose-400 hover:bg-rose-500/10"
                                title="Excluir do dicionário"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>

                          {/* Linha de Destino Interno e Botão Alterar Vínculo */}
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-2 border-t border-slate-800/80 bg-slate-900/40 p-2.5 rounded-lg">
                            <div className="flex items-center gap-2 min-w-0">
                              <ArrowRight className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                              <div className="min-w-0">
                                <span className="text-[10px] text-slate-400 block">
                                  Destino no Parque:
                                </span>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span
                                    className={`font-semibold text-xs truncate ${
                                      isDespesa ? "text-slate-400 italic" : "text-emerald-300"
                                    }`}
                                  >
                                    {itemInternoNome}
                                  </span>

                                  {!isDespesa && (() => {
                                    const itemEstoque = getItemEstoque(m.codigo_estoque_interno);
                                    const qtdTotal = Number(itemEstoque?.quantidadeAtual || 0);
                                    const qtdItem = getQtdContribuicaoProduto(m);
                                    const percent = qtdTotal > 0 ? Math.round((qtdItem / qtdTotal) * 100) : 0;

                                    return (
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        {/* TAG 1: QUANTIDADE DESTE CÓDIGO ESPECÍFICO */}
                                        <span
                                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold border shadow-sm shrink-0 ${
                                            qtdItem > 0
                                              ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
                                              : "bg-slate-800/80 text-slate-400 border-slate-700"
                                          }`}
                                          title={`Este código de produto forneceu ${qtdItem} un nas notas desta temporada (${percent}% do estoque geral)`}
                                        >
                                          <Tag className="w-3 h-3 text-amber-400" />
                                          <span>Deste Código: <strong>{qtdItem.toLocaleString("pt-BR")} un</strong>{qtdTotal > 0 ? ` (${percent}%)` : ""}</span>
                                        </span>

                                        {/* TAG 2: TOTAL DO ESTOQUE GERAL */}
                                        <span
                                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border shrink-0 ${
                                            qtdTotal > 0
                                              ? "bg-cyan-500/10 text-cyan-300 border-cyan-500/20"
                                              : "bg-slate-800/60 text-slate-400 border-slate-700/60"
                                          }`}
                                          title={`Estoque físico geral registrado para ${itemInternoNome}: ${qtdTotal} un`}
                                        >
                                          <Package className="w-3 h-3 text-cyan-400" />
                                          <span>Estoque Geral: {qtdTotal.toLocaleString("pt-BR")} un</span>
                                        </span>
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center justify-between sm:justify-end gap-2 shrink-0">
                              {!isDespesa && (
                                <span className="text-[11px] font-mono text-amber-400 bg-slate-950 px-2 py-1 rounded border border-slate-800">
                                  1 {m.unidade_fornecedor || "EB"} = <strong>{fatorAtual} UN</strong>
                                </span>
                              )}

                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => iniciarEdicao(m)}
                                className="h-7 text-xs border-emerald-500/30 hover:border-emerald-500 text-slate-300 hover:text-white hover:bg-emerald-500/10 flex items-center gap-1.5"
                              >
                                <Edit2 className="w-3 h-3 text-emerald-400" />
                                <span>Alterar Vínculo</span>
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        /* Formulário de Edição Inline */
                        <div className="space-y-3 p-1">
                          <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                            <div>
                              <span className="text-[10px] text-emerald-400 uppercase tracking-wider font-semibold">
                                Editando Mapeamento
                              </span>
                              <p className="font-bold text-slate-200 text-sm">
                                {m.nome_produto_fornecedor}
                              </p>
                            </div>
                            <span className="text-[11px] font-mono text-slate-400">
                              Cód: {m.codigo_fornecedor}
                            </span>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                            {/* Produto Interno */}
                            <div className="sm:col-span-2">
                              <label className="text-[10px] text-slate-400 block mb-1">
                                Vincular a Produto do Estoque:
                              </label>
                              <select
                                value={formEdicao.codigoInterno}
                                onChange={(e) => {
                                  const novoCod = e.target.value;
                                  setFormEdicao({
                                    ...formEdicao,
                                    codigoInterno: novoCod,
                                    categoriaContabil:
                                      novoCod === "DESP_GERAL" ? "INSUMO" : "REVENDA",
                                  });
                                }}
                                className="w-full h-8 px-2 text-xs rounded-md bg-slate-900 border border-emerald-500/60 text-slate-200 focus:ring-1 focus:ring-emerald-500"
                              >
                                <option value="DESP_GERAL">
                                  ❌ Não mapear (Despesa Geral / Sem Estoque Físico)
                                </option>
                                <optgroup label="Produtos Cadastrados no Estoque">
                                  {listaEstoque.map((item) => {
                                    const qtd = Number(item.quantidadeAtual || 0).toLocaleString("pt-BR");
                                    return (
                                      <option key={item.id || item.codigo} value={item.codigo}>
                                        {item.nome} (Cód: {item.codigo} • Estoque: {qtd} un)
                                      </option>
                                    );
                                  })}
                                </optgroup>
                              </select>

                              {formEdicao.codigoInterno && formEdicao.codigoInterno !== "DESP_GERAL" && (() => {
                                const itemEst = getItemEstoque(formEdicao.codigoInterno);
                                if (!itemEst) return null;
                                const qtd = Number(itemEst.quantidadeAtual || 0);
                                return (
                                  <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-400">
                                    <span>Saldo físico atual:</span>
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                                      <Package className="w-3 h-3 text-cyan-400" />
                                      <span>{qtd.toLocaleString("pt-BR")} un</span>
                                    </span>
                                  </div>
                                );
                              })()}
                            </div>

                            {/* Categoria Contábil */}
                            <div>
                              <label className="text-[10px] text-slate-400 block mb-1">
                                Finalidade:
                              </label>
                              <select
                                value={formEdicao.categoriaContabil}
                                disabled={formEdicao.codigoInterno === "DESP_GERAL"}
                                onChange={(e) =>
                                  setFormEdicao({
                                    ...formEdicao,
                                    categoriaContabil: e.target.value,
                                  })
                                }
                                className="w-full h-8 px-2 text-xs rounded-md bg-slate-900 border border-slate-800 text-slate-200 focus:ring-1 focus:ring-emerald-500"
                              >
                                <option value="REVENDA">🛒 Mercadoria Revenda</option>
                                <option value="INSUMO">🏢 Insumo / Despesa</option>
                              </select>
                            </div>

                            {/* Fator de Conversão */}
                            {formEdicao.codigoInterno !== "DESP_GERAL" && (
                              <div className="sm:col-span-3 bg-slate-900/60 p-2.5 rounded-lg border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <div className="space-y-0.5">
                                  <label className="text-xs font-semibold text-slate-300 block">
                                    Fator de Conversão de Embalagem
                                  </label>
                                  <p className="text-[11px] text-slate-400">
                                    Se o fornecedor vendeu em dúzia ou caixa, quantas unidades físicas entram no bar?
                                  </p>
                                </div>

                                <div className="flex items-center gap-1.5 shrink-0">
                                  <span className="text-[11px] text-slate-400">
                                    1 {m.unidade_fornecedor || "EB"} =
                                  </span>
                                  <Input
                                    type="number"
                                    min="1"
                                    value={formEdicao.fatorConversao}
                                    onChange={(e) =>
                                      setFormEdicao({
                                        ...formEdicao,
                                        fatorConversao: e.target.value,
                                      })
                                    }
                                    className="w-20 h-7 text-xs bg-slate-950 border-emerald-500 text-emerald-300 text-center font-bold"
                                  />
                                  <span className="text-[11px] text-slate-400">UN</span>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Botões de Ação da Edição */}
                          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-800">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={cancelarEdicao}
                              className="h-8 text-xs text-slate-400 hover:text-slate-200"
                            >
                              <X className="w-3.5 h-3.5 mr-1" />
                              Cancelar
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleSalvarEdicao(m)}
                              className="h-8 text-xs bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold flex items-center gap-1.5 shadow-md shadow-emerald-500/20"
                            >
                              <Check className="w-3.5 h-3.5" />
                              Salvar Alterações
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* CONTEÚDO DA ABA 2: AUDITORIA DE NOTAS PASSADAS */}
        {abaAtiva === "auditoria" && (
          <div className="flex-1 overflow-hidden flex flex-col p-4 sm:p-6 pt-3 space-y-3">
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-xs text-amber-200 flex items-start gap-2.5">
              <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-300">
                  Conciliação e Auditoria Retroativa de Estoque
                </p>
                <p className="text-[11px] text-amber-200/80 mt-0.5">
                  Esta auditoria cruza o histórico de notas com as regras atuais do seu dicionário.
                  Se você mudou um item de "Não Mapear" para "Revenda" ou corrigiu o fator de embalagem,
                  as unidades faltantes na <strong>Temporada Atual</strong> podem ser creditadas com 1 clique.
                </p>
              </div>
            </div>

            {/* Demarcação de Temporada e Filtro */}
            {dataCorte && (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-slate-950/80 px-3.5 py-2.5 rounded-xl border border-slate-800 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
                  <span className="text-slate-300 text-[11px] sm:text-xs">
                    Temporada Atual ativa iniciada em <strong>{dataCorte.toLocaleDateString("pt-BR")}</strong> ({ultimaTemporadaFechada?.nome || "Último Fechamento"}).
                  </span>
                </div>

                {itensAuditoriaFechados.length > 0 && (
                  <label className="flex items-center gap-2 cursor-pointer text-slate-400 hover:text-slate-200 text-xs select-none shrink-0">
                    <input
                      type="checkbox"
                      checked={mostrarTodasTemporadas}
                      onChange={(e) => setMostrarTodasTemporadas(e.target.checked)}
                      className="rounded border-slate-700 bg-slate-900 text-emerald-500 focus:ring-0 w-3.5 h-3.5 cursor-pointer"
                    />
                    <span>Ver notas de temporadas anteriores ({itensAuditoriaFechados.length})</span>
                  </label>
                )}
              </div>
            )}

            <div className="flex-1 overflow-y-auto space-y-2.5 pr-1">
              {itensAuditoriaExibidos.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-xs border border-dashed border-slate-800 rounded-xl p-6 flex flex-col items-center justify-center">
                  <ShieldCheck className="w-8 h-8 text-emerald-400 mb-2" />
                  <span className="font-semibold text-slate-200 text-sm">
                    Estoque da Temporada Atual 100% Conciliado!
                  </span>
                  <span className="text-slate-500 text-xs mt-1 max-w-sm text-center">
                    Não há notas pendentes de ajuste nesta temporada. As notas de temporadas passadas já tiveram seu estoque arquivado e zerado.
                  </span>
                </div>
              ) : (
                itensAuditoriaExibidos.map((it, idx) => {
                  const nomeInterno = getNomeItemInterno(it.codigoInterno);
                  const chaveResumo = `...${it.chaveNota.slice(-8)}`;
                  const isEraInsumo = it.tipoDiscrepancia === "era_insumo";
                  const isFechado = it.pertenceTemporadaFechada;

                  return (
                    <div
                      key={`${it.chaveNota}-${it.codigoFornecedor}-${idx}`}
                      className={`rounded-xl p-3.5 text-xs space-y-2.5 shadow-sm transition-all ${
                        isFechado
                          ? "bg-slate-950/40 border border-slate-800 opacity-75"
                          : "bg-slate-950/70 border border-amber-500/30"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2 text-[10px] font-mono text-slate-500">
                            <span>Nota: {chaveResumo}</span>
                            <span>•</span>
                            <span>
                              Emissão:{" "}
                              {it.dataEmissao
                                ? new Date(it.dataEmissao).toLocaleDateString("pt-BR")
                                : "N/D"}
                            </span>
                            {isFechado && (
                              <span className="text-slate-400 bg-slate-800 px-1.5 py-0.2 rounded font-sans">
                                (Temporada Anterior)
                              </span>
                            )}
                          </div>
                          <h4 className="font-bold text-slate-200 text-sm">
                            {it.nomeProduto}
                          </h4>
                          <div className="flex items-center gap-2 flex-wrap pt-0.5">
                            <span className="text-[11px] text-slate-400">
                              Vínculo Atual:{" "}
                              <strong className="text-emerald-400">{nomeInterno}</strong>
                            </span>
                            {it.codigoInterno && it.codigoInterno !== "DESP_GERAL" && (() => {
                              const itemEst = getItemEstoque(it.codigoInterno);
                              const qtdTotal = Number(itemEst?.quantidadeAtual || 0);
                              return (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 shadow-sm"
                                  title="Estoque físico geral atual do produto no parque"
                                >
                                  <Package className="w-3 h-3 text-cyan-400" />
                                  <span>Estoque Geral: {qtdTotal.toLocaleString("pt-BR")} un</span>
                                </span>
                              );
                            })()}
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-1">
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded border ${
                              isFechado
                                ? "bg-slate-800 text-slate-400 border-slate-700"
                                : isEraInsumo
                                ? "bg-rose-500/20 text-rose-300 border-rose-500/30"
                                : "bg-amber-500/20 text-amber-300 border-amber-500/30"
                            }`}
                          >
                            {isFechado
                              ? "🔒 Temporada Fechada"
                              : isEraInsumo
                              ? "⚠️ Era Não Mapeado (0 un)"
                              : `📦 Fator ${it.fatorIdeal}x`}
                          </span>
                        </div>
                      </div>

                      {/* Comparativo Numérico */}
                      <div className="grid grid-cols-3 gap-2 bg-slate-900/80 p-2.5 rounded-lg border border-slate-800 text-center">
                        <div>
                          <span className="text-[10px] text-slate-500 block">
                            Entrou Anteriormente
                          </span>
                          <span className="font-semibold text-slate-400 text-xs">
                            {it.qtdEstoqueGravada} un
                          </span>
                        </div>
                        <div>
                          <span className="text-[10px] text-slate-500 block">
                            Quantidade Correta
                          </span>
                          <span className="font-bold text-emerald-400 text-xs">
                            {it.qtdIdeal} un
                          </span>
                        </div>
                        <div>
                          <span className="text-[10px] text-amber-400 font-medium block">
                            Crédito a Lançar
                          </span>
                          <span className="font-extrabold text-amber-300 text-xs">
                            +{it.deltaAjustar} un
                          </span>
                        </div>
                      </div>

                      {/* Botão de Aplicação Imediata ou Bloqueio */}
                      <div className="flex justify-end pt-1">
                        {isFechado ? (
                          <div className="flex items-center justify-between w-full pt-1">
                            <span className="text-[11px] text-slate-500 flex items-center gap-1.5 italic">
                              <Lock className="w-3.5 h-3.5 text-slate-500" />
                              Arquivada no encerramento de {it.dataCorteFormatada} ({it.nomeTemporadaFechada})
                            </span>
                            <Button
                              size="sm"
                              disabled
                              className="bg-slate-800 text-slate-500 border border-slate-700/60 cursor-not-allowed text-xs h-8 px-3 opacity-60 flex items-center gap-1.5"
                              title="Esta nota pertence à temporada passada. O estoque daquela época já foi gravado no histórico e zerado."
                            >
                              <Lock className="w-3.5 h-3.5" />
                              Estoque Já Encerrado
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => handleCorrigirItemAuditoria(it)}
                            className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs h-8 px-3 shadow-md shadow-amber-500/10 flex items-center gap-1.5"
                          >
                            <Sparkles className="w-3.5 h-3.5" />
                            {isEraInsumo
                              ? `Creditar no Estoque (+${it.deltaAjustar} un)`
                              : `Ajustar Dúzia (+${it.deltaAjustar} un)`}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DicionarioMapeamentoModal;
